import React from 'react';

import { PlusIcon } from '@vlabs/icons';
import { useModal } from '@vlabs/uikit';
import { Button } from '@vlabs/uikit/components/controls/button';
import type { ButtonProps } from '@vlabs/uikit/components/controls/button';

import { buildTestid } from '@vlabs/monokit/shared/lib/utils';

import { CreateDepartmentForm } from './CreateDepartmentForm';
import { useCreateDepartmentTranslation } from './i18n';
import { useCreateDepartmentSubmit } from './useCreateDepartmentSubmit';
import type { UseCreateDepartmentSubmitParams } from './useCreateDepartmentSubmit';

type CreateDepartmentButtonProps = UseCreateDepartmentSubmitParams & Omit<ButtonProps, 'onClick'>;

const displayName = 'CreateDepartmentButton';
const testid = buildTestid(displayName);

function CreateDepartmentButton({
  parentDepartmentId = undefined,
  onCreateDepartment = undefined,
  'data-testid': dataTestId = undefined,
  ...props
}: CreateDepartmentButtonProps): React.JSX.Element {
  const { t } = useCreateDepartmentTranslation();
  const modal = useModal({ size: 'small' });
  const handleSubmit = useCreateDepartmentSubmit({
    parentDepartmentId,
    onCreateDepartment,
    onSuccess: modal.close,
  });

  return (
    <React.Fragment>
      {modal.wrap(
        <CreateDepartmentForm
          onCancel={modal.close}
          onSubmit={handleSubmit}
        />,
      )}

      <Button
        icon={<PlusIcon />}
        onClick={modal.open}
        variant="outlined"
        {...testid(dataTestId)}
        {...props}
      >
        {props.children ?? t('add_button')}
      </Button>
    </React.Fragment>
  );
}

CreateDepartmentButton.displayName = displayName;

export { CreateDepartmentButton };
